import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { CollectionSheetData } from '../collection-sheet/collection-sheet.component';

/**
 * Collections for one customer — the A/R collection tracker at customer scope.
 *
 * Left of the line: live aging off open `debtortrans` (what they owe and how
 * late). Right of it: the collection trail — every call/email, what the customer
 * promised, and when to chase them again. A note can be pinned to one open
 * document so "she disputes invoice 40112" survives past the phone call.
 *
 * Embedded as the Collection tab on the customer view and reachable from the
 * A/R Collection Tracker worklist.
 */
@Component({
	selector: 'app-collection-notes',
	templateUrl: './collection-notes.component.html',
	styleUrls: ['./collection-notes.component.scss']
})
export class CollectionNotesComponent implements OnInit, OnChanges {
	/** Customer view payload — `debtorno` / `branchcode` are read off it. */
	@Input() customerdata: any;
	/** Use instead of `customerdata` when only the account number is known. */
	@Input() debtorno = '';

	@Output() changed = new EventEmitter<void>();

	loading = false;
	/** False until migrations/ar_collection_notes.sql has been run on this database. */
	installed = true;
	installMessage = '';

	customer: any = null;
	aging: any = null;
	openItems: any[] = [];
	notes: any[] = [];

	contactTypes = [
		{ value: 'call', label: 'Phone call' },
		{ value: 'voicemail', label: 'Voicemail' },
		{ value: 'email', label: 'Email' },
		{ value: 'letter', label: 'Letter' },
		{ value: 'portal', label: 'Portal message' },
		{ value: 'visit', label: 'Visit' },
		{ value: 'note', label: 'Internal note' },
	];

	outcomes = [
		{ value: 'promise', label: 'Promised to pay' },
		{ value: 'partial', label: 'Promised partial' },
		{ value: 'plan', label: 'Payment plan' },
		{ value: 'paid', label: 'Says already paid' },
		{ value: 'dispute', label: 'Disputes the charge' },
		{ value: 'no_answer', label: 'No answer' },
		{ value: 'message', label: 'Left message' },
		{ value: 'refused', label: 'Refused to pay' },
		{ value: 'escalated', label: 'Escalated' },
		{ value: 'other', label: 'Other' },
	];

	statuses = [
		{ value: 'open', label: 'Open — still chasing' },
		{ value: 'kept', label: 'Kept — they paid' },
		{ value: 'broken', label: 'Broken promise' },
		{ value: 'closed', label: 'Closed' },
	];

	/** Non-null while the collections flyout is open. */
	sheet: CollectionSheetData | null = null;

	constructor(
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
	) {}

	ngOnInit(): void {
		this.load();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.customerdata || changes.debtorno) {
			this.load();
		}
	}

	get accountNo(): string {
		return this.debtorno || (this.customerdata ? this.customerdata.debtorno : '');
	}

	get branchNo(): string {
		return this.customerdata && this.customerdata.branchcode ? this.customerdata.branchcode : '';
	}

	get today(): string {
		return new Date().toISOString().substring(0, 10);
	}

	/** Worst populated bucket — drives the header tile colour. */
	get tone(): string {
		if (!this.aging) { return ''; }
		if (Number(this.aging.b_90plus) > 0 || Number(this.aging.b_61_90) > 0) { return 'is-failed'; }
		if (Number(this.aging.b_31_60) > 0 || Number(this.aging.b_1_30) > 0) { return 'is-partial'; }
		return 'is-settled';
	}

	get overdue(): number {
		if (!this.aging) { return 0; }
		return Number(this.aging.b_1_30) + Number(this.aging.b_31_60)
			+ Number(this.aging.b_61_90) + Number(this.aging.b_90plus);
	}

	/** Sum still promised on open notes whose promise date hasn't passed. */
	get promisedOpen(): number {
		return this.notes
			.filter(n => n.status === 'open' && n.promise_date && n.promise_date >= this.today)
			.reduce((a, n) => a + Number(n.promise_amount || 0), 0);
	}

	get brokenPromises(): any[] {
		return this.notes.filter(n => n.status === 'open' && n.promise_date && n.promise_date < this.today);
	}

	/** Soonest open follow-up, past or future — what collections should do next. */
	get nextFollowup(): any {
		const open = this.notes
			.filter(n => n.status === 'open' && n.followup_date)
			.sort((a, b) => (a.followup_date < b.followup_date ? -1 : 1));
		return open.length ? open[0] : null;
	}

	load(silent = false) {
		const account = this.accountNo;
		if (!account) { return; }
		this.loading = !silent;
		this.paymentsService.getCollectionCustomer(account).subscribe((r: any) => {
			this.loading = false;
			if (!r || !r.success) {
				this.globalSearchService.showNotification(
					(r && r.message) || 'Could not load collections', 'danger', 'bottom', 'left'
				);
				return;
			}
			this.installed = !!r.installed;
			this.installMessage = r.message || '';
			this.customer = r.customer || null;
			this.aging = r.aging || null;
			this.openItems = r.open_items || [];
			this.notes = r.notes || [];
		});
	}

	/**
	 * Hand off to the shared collections flyout. Everything about writing a note
	 * lives there — one form, one collision warning, one payment panel — so this
	 * tab stays the deep read-only view of the account.
	 */
	openSheet(item: any = null, note: any = null) {
		this.sheet = {
			debtorno: this.accountNo,
			branchcode: item ? item.branchcode : this.branchNo,
			transno: item ? item.transno : 0,
			trans_type: item ? item.type : 0,
			note: note,
		};
	}

	onSheetClosed(wrote: boolean) {
		this.sheet = null;
		if (wrote) {
			this.load(true);
			this.changed.emit();
		}
	}

	/** `tel:` needs bare digits, not the spaces people type into the field. */
	telHref(phone: string): string {
		return 'tel:' + String(phone || '').replace(/[^\d+]/g, '');
	}

	statusPill(status: string): 'settled' | 'partial' | 'failed' | 'neutral' {
		switch (status) {
		case 'kept': return 'settled';
		case 'broken': return 'failed';
		case 'closed': return 'neutral';
		default: return 'partial';
		}
	}

	label(list: any[], value: string): string {
		const hit = list.find(o => o.value === value);
		return hit ? hit.label : value;
	}

	/** A promise whose date has passed and that nobody has closed out. */
	isBroken(note: any): boolean {
		return note.status === 'open' && !!note.promise_date && note.promise_date < this.today;
	}

	bucketOf(days: number): string {
		if (days <= 0) { return 'Current'; }
		if (days <= 30) { return '1-30'; }
		if (days <= 60) { return '31-60'; }
		if (days <= 90) { return '61-90'; }
		return '90+';
	}
}
